import React, { useEffect, useState } from 'react'
import { CircularProgress, IconButton } from '@material-ui/core'
import GetAppIcon from '@material-ui/icons/GetApp'
import { BarChart } from '@tremor/react'

import moment, { Moment } from 'config/moment'
import { useApiCall } from 'hooks'
import { montserratStyle } from 'features/Main/NewDashboard/styles'

import { useLoadingTracker } from '../../hooks/useLoadingTracker'
import { formatDashboardNumber } from '../../utils/formatDashboardNumber'
import { getScansByUserLevel, ScansByUserLevelParams, ScansByUserLevelResponse } from './api'

interface LevelRegistrationEvolutionProps {
  // eslint-disable-next-line @typescript-eslint/member-delimiter-style
  period1: { startDate: Moment; endDate: Moment }
  trigger: number
  storeIds?: number[]
  // eslint-disable-next-line react/require-default-props
  onLoaded?: () => void
}

interface ChartData {
  mes: string
  [key: string]: string | number // Dynamic level properties
}

interface LevelInfo {
  name: string
  level: number
}

export const LevelRegistrationEvolution: React.FC<LevelRegistrationEvolutionProps> = ({
  period1,
  trigger,
  storeIds,
  onLoaded,
}) => {
  const [data, setData] = useState<ChartData[]>([])
  const [levels, setLevels] = useState<LevelInfo[]>([])
  const [loading, setLoading] = useState(false)

  // Track loading state
  useLoadingTracker({ isLoading: loading, componentId: 'LevelRegistrationEvolution' })

  const endDate = period1.endDate.format('YYYY-MM-DD')

  const [fetchData] = useApiCall<ScansByUserLevelParams, ScansByUserLevelResponse>(
    getScansByUserLevel,
  )

  const loadData = async () => {
    setLoading(true)
    try {
      const response = await fetchData({
        endDate,
        ...(storeIds && storeIds.length > 0 && { storeIds: storeIds.join(',') }),
      })
      if (response?.data) {
        const { dates, levels: levelsData } = response.data
        setLevels(levelsData)

        // Transform the data to match the chart format dynamically
        const transformedData = dates.map((item: ScansByUserLevelResponse['data']['dates'][0]) => {
          const chartItem: ChartData = {
            mes: moment(item.date).format('MMMM'),
          }

          // Dynamically add level data based on available levels
          levelsData.forEach(levelInfo => {
            const levelKey = `nivel${levelInfo.level}`
            chartItem[levelKey] = item[`level_${levelInfo.level}`] || 0
          })

          return chartItem
        })
        setData(transformedData)
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
      if (onLoaded) {
        onLoaded()
      }
    }
  }

  useEffect(() => {
    if (trigger > 0) {
      loadData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  const downloadCSV = () => {
    const headers = ['Mes', ...levels.map(level => level.name)]
    const csvRows = [
      headers,
      ...data.map(row => [row.mes, ...levels.map(level => row[`nivel${level.level}`] || 0)]),
    ]
    const csvContent = csvRows.map(e => e.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = 'evolucion_registros_nivel.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  /* if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <span className="font-bold text-base block mb-4" style={montserratStyle}>
          Cargando datos...
        </span>
      </div>
    )
  } */

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex flex-row justify-between items-start mb-2">
        <div>
          <span className="font-bold text-base block mb-4" style={montserratStyle}>
            Evolución de registros por nivel
          </span>
          <div className="flex gap-4 items-center mb-2">
            {!loading &&
              levels.map((level, index) => {
                const colors = ['green', 'emerald', 'lime', 'sky', 'teal']
                const color = colors[index % colors.length]

                return (
                  <div key={level.level} className="flex items-center gap-2">
                    <span
                      className={`bg-${color}-500`}
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 4,
                        display: 'inline-block',
                      }}
                    />
                    <span style={montserratStyle}>{level.name}</span>
                  </div>
                )
              })}
          </div>
        </div>
        <IconButton disabled={loading} className="ml-2" aria-label="download" onClick={downloadCSV}>
          <GetAppIcon />
        </IconButton>
      </div>
      <div className="mt-4 overflow-x-auto">
        <div style={{ minWidth: 600 }}>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <CircularProgress size={40} />
            </div>
          ) : (
            <BarChart
              data={data}
              index="mes"
              categories={levels.map(level => `nivel${level.level}`)}
              colors={['green', 'emerald', 'lime', 'sky', 'teal']}
              showLegend={false}
              showGridLines
              yAxisWidth={60}
              stack
              valueFormatter={(value: number) => formatDashboardNumber(value)}
              style={{ fontFamily: 'Montserrat, sans-serif' }}
              noDataText="No hay datos disponibles para mostrar en este momento."
            />
          )}
        </div>
      </div>
    </div>
  )
}

LevelRegistrationEvolution.defaultProps = {
  storeIds: undefined,
}
